/**
 * useAuth - Authentication hook
 * 
 * Manages:
 * - Login/logout state
 * - Token storage
 * - User info
 * - Auto-refresh
 */
import { useState, useEffect, useCallback, createContext, useContext } from 'react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

// Auth context
const AuthContext = createContext(null);

// Token storage keys
const ACCESS_TOKEN_KEY = 'riskee_access_token';
const REFRESH_TOKEN_KEY = 'riskee_refresh_token';
const USER_KEY = 'riskee_user';

/**
 * Get stored tokens
 */
function getStoredTokens() {
  return {
    accessToken: localStorage.getItem(ACCESS_TOKEN_KEY),
    refreshToken: localStorage.getItem(REFRESH_TOKEN_KEY),
  };
}

/**
 * Get stored user
 */
function getStoredUser() {
  const userJson = localStorage.getItem(USER_KEY);
  if (userJson) {
    try {
      return JSON.parse(userJson);
    } catch {
      return null;
    }
  }
  return null;
}

/**
 * Store auth data
 */
function storeAuth(data) {
  localStorage.setItem(ACCESS_TOKEN_KEY, data.access_token);
  localStorage.setItem(REFRESH_TOKEN_KEY, data.refresh_token);
  localStorage.setItem(USER_KEY, JSON.stringify(data.user));
}

/**
 * Clear auth data and user-scoped caches
 */
function clearAuth() {
  localStorage.removeItem(ACCESS_TOKEN_KEY);
  localStorage.removeItem(REFRESH_TOKEN_KEY);
  localStorage.removeItem(USER_KEY);
  
  // Clear user-scoped offline data to prevent cross-user reads
  localStorage.removeItem('riskee_pending_writes');
  localStorage.removeItem('riskee_reauth_required');
  localStorage.removeItem('riskee_sync_telemetry');
  localStorage.removeItem('riskee_failed_permissions');
  
  // Tell service worker to clear caches for this user
  if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
    navigator.serviceWorker.controller.postMessage({ type: 'CLEAR_USER_DATA' });
  }
}

/**
 * Auth Provider component
 */
export function AuthProvider({ children }) {
  const [user, setUser] = useState(getStoredUser);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Check if user is authenticated
  const isAuthenticated = !!user;

  // Get access token for API calls
  const getAccessToken = useCallback(() => {
    return localStorage.getItem(ACCESS_TOKEN_KEY);
  }, []);

  // Login
  const login = useCallback(async (email, password) => {
    setLoading(true);
    setError(null);
    
    try {
      const res = await fetch(`${API}/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password }),
      });
      
      const data = await res.json();
      
      if (!res.ok) {
        throw new Error(data.detail || 'Login failed');
      }
      
      storeAuth(data);
      setUser(data.user);
      return data.user;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  // Register
  const register = useCallback(async (email, password, displayName) => {
    setLoading(true);
    setError(null);
    
    try {
      const res = await fetch(`${API}/auth/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password, display_name: displayName }),
      });
      
      const data = await res.json();
      
      if (!res.ok) {
        throw new Error(data.detail || 'Registration failed');
      }
      
      storeAuth(data);
      setUser(data.user);
      return data.user;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  // Logout
  const logout = useCallback(() => {
    clearAuth();
    setUser(null);
  }, []);

  // Refresh token
  const refreshToken = useCallback(async () => {
    const { refreshToken: token } = getStoredTokens();
    if (!token) return false;
    
    try {
      const res = await fetch(`${API}/auth/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refresh_token: token }),
      });
      
      if (!res.ok) {
        clearAuth();
        setUser(null);
        return false;
      }
      
      const data = await res.json();
      storeAuth(data);
      setUser(data.user);
      return true;
    } catch {
      clearAuth();
      setUser(null);
      return false;
    }
  }, []);

  // Verify stored token on mount
  useEffect(() => {
    const verifyAuth = async () => {
      const { accessToken } = getStoredTokens();
      
      if (!accessToken) {
        setLoading(false);
        return;
      }
      
      try {
        const res = await fetch(`${API}/auth/me`, {
          headers: { 'Authorization': `Bearer ${accessToken}` },
        });
        
        if (res.ok) {
          const userData = await res.json();
          setUser(userData);
        } else {
          // Try refresh
          const refreshed = await refreshToken();
          if (!refreshed) {
            clearAuth();
            setUser(null);
          }
        }
      } catch {
        clearAuth();
        setUser(null);
      } finally {
        setLoading(false);
      }
    };
    
    verifyAuth();
  }, [refreshToken]);

  // Role helpers
  const hasRole = useCallback((role) => {
    if (!user?.roles) return false;
    if (user.roles.includes('ORG_ADMIN')) return true;
    return user.roles.includes(role);
  }, [user]);

  const hasAnyRole = useCallback((roles) => {
    if (!user?.roles) return false;
    if (user.roles.includes('ORG_ADMIN')) return true;
    return roles.some(role => user.roles.includes(role));
  }, [user]);

  const primaryRole = user?.roles?.length > 0 
    ? user.roles[user.roles.length - 1] 
    : 'REPORTER';

  const value = {
    user,
    loading,
    error,
    isAuthenticated,
    login,
    register,
    logout,
    refreshToken,
    getAccessToken,
    hasRole,
    hasAnyRole,
    primaryRole,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

/**
 * useAuth hook
 */
export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
}

/**
 * Authenticated fetch helper
 */
export async function authFetch(endpoint, options = {}) {
  const token = localStorage.getItem(ACCESS_TOKEN_KEY);
  
  const headers = {
    'Content-Type': 'application/json',
    ...options.headers,
  };
  
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  
  const res = await fetch(`${API}${endpoint}`, {
    ...options,
    headers,
  });
  
  // Handle 401 - token expired
  if (res.status === 401) {
    clearAuth();
    window.location.href = '/login';
    throw new Error('Session expired');
  }
  
  return res;
}

export default useAuth;
